import {
  prepareDoudianMainImageMaterials,
  saveDoudianMainImageRow,
  getDoudianBatchDetail
} from '@/api/main/doudianBatchEdit'

const unwrap = (r) => (r?.data?.data !== undefined ? r.data.data : r?.data !== undefined ? r.data : r)
const equal = (a, b) => JSON.stringify(a) === JSON.stringify(b)
const requestKey = () => crypto.randomUUID()
export default {
  data: () => ({ inlineRows: {}, inlineClock: Date.now(), inlineClockTimer: null, draggingImages: false }),
  mounted() {
    this.inlineClockTimer = setInterval(() => {
      this.inlineClock = Date.now()
    }, 1000)
  },
  beforeDestroy() {
    clearInterval(this.inlineClockTimer)
  },
  methods: {
    inlineScope() {
      return JSON.stringify({ ...this.filterPayload(), excluded_product_ids: [] })
    },
    reconcileInlineScope() {
      ;(this.candidates?.list || []).forEach((item) => {
        const row = this.inlineRows[this.productKey(item)]
        if (row && !this.inlineBusy(row)) this.resetInline(item)
      })
    },
    inlineState(item) {
      const key = this.productKey(item)
      if (!this.inlineRows[key]) this.resetInline(item)
      const row = this.inlineRows[key]
      if (!equal(row.base, item.old_images || []) && !this.inlineBusy(row)) {
        if (this.inlineDirty(row)) {
          row.error = '原图已变化，请重新加载后编辑'
          row.conflict = true
        } else this.resetInline(item)
      }
      return this.inlineRows[key]
    },
    resetInline(item) {
      this.$set(this.inlineRows, this.productKey(item), {
        base: [...(item.old_images || [])],
        images: (item.old_images || []).map((url, index) => ({
          key: `old:${index}`,
          kind: 'existing',
          origin_index: index,
          url
        })),
        scope: this.inlineScope(),
        state: 'idle',
        error: '',
        conflict: false,
        preparing: false,
        materials: {},
        nextRefresh: 0,
        version: 0,
        saveRequest: null,
        batchId: null
      })
    },
    inlineBusy(row) {
      return row.state === 'saving' || row.state === 'confirming' || row.state === 'uncertain'
    },
    inlineUrls(row) {
      return row.images.map((img) =>
        img.kind === 'existing'
          ? row.base[img.origin_index]
          : row.materials[img.source_id]?.byte_url || this.mediaFor(img.mediaKey)?.preview_url || img.url
      )
    },
    inlineDirty(row) {
      return !equal(this.inlineUrls(row), row.base)
    },
    inlineChangeType(item, img, index) {
      const row = this.inlineState(item)
      const origin = img.kind === 'existing' ? img.origin_index : img.replaced_origin_index
      const url = this.inlineUrls(row)[index]
      if (origin === undefined) return 'added'
      if (url !== row.base[origin]) return 'replaced'
      return url === row.base[index] ? 'unchanged' : 'moved'
    },
    inlineDeletedImages(item) {
      const row = this.inlineState(item)
      const retained = new Set(
        row.images.map((img) => (img.kind === 'existing' ? img.origin_index : img.replaced_origin_index))
      )
      return row.base
        .map((url, index) => ({ key: `deleted:${index}`, url }))
        .filter((img, index) => !retained.has(index))
    },
    inlineChanged(item) {
      const row = this.inlineState(item)
      row.version++
      row.error = ''
      row.state = 'idle'
      row.saveRequest = null
      row.batchId = null
      this.saveDraft()
    },
    inlineCanSave(item) {
      const row = this.inlineState(item),
        urls = this.inlineUrls(row)
      return (
        row.scope === this.inlineScope() &&
        !(item.preview_result === 'skipped' && item.reason !== 'no_change') &&
        !this.isExcluded(item) &&
        !row.conflict &&
        !row.preparing &&
        !this.inlineBusy(row) &&
        this.inlineDirty(row) &&
        urls.length >= 3 &&
        urls.length <= 10 &&
        new Set(urls).size >= 3 &&
        row.images.every(
          (img) => img.kind === 'existing' || (img.source_id && row.materials[img.source_id]?.can_submit)
        )
      )
    },
    inlineLabel(item) {
      if (this.isExcluded(item)) return '已排除'
      const row = this.inlineState(item)
      if (item.preview_result === 'skipped' && item.reason !== 'no_change') return '跳过'
      if (row.state === 'saving') return '保存中…'
      if (['confirming', 'uncertain'].includes(row.state)) return '结果待确认'
      if (row.state === 'completed') return '保存成功'
      if (row.state === 'failed') return '保存失败'
      return this.inlineDirty(row) ? '将修改' : '无变化'
    },
    openInlineImage(item, image = null) {
      const row = this.inlineState(item)
      if (this.inlineBusy(row) || row.conflict || row.preparing || this.uploading || this.isExcluded(item)) return
      if (!image && row.images.length >= 10) return this.$message.warning('主图最多10张')
      this.imageSelectorTarget = {
        method: 1,
        rowKey: this.productKey(item),
        imageKey: image?.key || null,
        version: row.version
      }
      this.imageSelectorVisible = true
    },
    async deleteInlineImage(item, image) {
      const row = this.inlineState(item)
      if (this.inlineBusy(row) || row.preparing || row.conflict) return
      if (row.images.length <= 3) return this.$message.warning('主图至少保留3张')
      const version = row.version
      try {
        await this.$confirm('确定删除这张图片？', '提示', { type: 'warning' })
      } catch {
        return
      }
      if (this._isDestroyed || row.version !== version || this.inlineBusy(row)) return
      row.images = row.images.filter((img) => img.key !== image.key)
      this.inlineChanged(item)
    },
    async acceptInlineSources(sources, target) {
      const item = this.candidates?.list?.find((value) => this.productKey(value) === target.rowKey)
      if (!item) return this.$message.warning('编辑商品已变化，请重新选图')
      const row = this.inlineState(item)
      if (this.selectedMethod !== 1 || row.version !== target.version || this.inlineBusy(row))
        return this.$message.warning('图片配置已变化，请重新选图')
      const selected = target.imageKey ? sources.slice(0, 1) : sources
      if (!selected.length) return
      if (row.images.length + (target.imageKey ? 0 : selected.length) > 10) return this.$message.warning('主图最多10张')
      const images = selected.map((source) => {
        const media = {
          key: source.client_key,
          id: source.client_key,
          source_id: null,
          name: '图片素材',
          source_status: 'registering',
          preview_url: source.preview_url,
          task_error: '',
          request: {
            source_kind: source.source_kind,
            source_ref: source.source_ref,
            idempotency_key: source.client_key
          }
        }
        this.media.push(media)
        return {
          key: `new:${source.client_key}`,
          kind: 'new',
          mediaKey: media.key,
          source_id: null,
          url: source.preview_url
        }
      })
      if (target.imageKey) {
        const index = row.images.findIndex((img) => img.key === target.imageKey)
        if (index < 0) return
        const original = row.images[index]
        images[0].replaced_origin_index =
          original.kind === 'existing' ? original.origin_index : original.replaced_origin_index
        row.images.splice(index, 1, ...images)
      } else row.images.push(...images)
      this.inlineChanged(item)
      await Promise.all(
        images.map(async (img) => {
          const media = this.mediaFor(img.mediaKey)
          await this.registerItem(media)
          img.source_id = media.source_id
        })
      )
      this.saveDraft()
      this.scheduleStatusPoll()
      this.prepareInline(item)
    },
    inlineInput(item) {
      const row = this.inlineState(item),
        filter = this.filterPayload()
      return {
        distribute_product_id: Number(item.distribute_product_id || item.id),
        shop_id: Number(item.shop_id),
        platform_product_id: String(item.platform_product_id),
        base_images: [...row.base],
        product_filter: JSON.parse(JSON.stringify(filter))
      }
    },
    async prepareInline(item, refresh = false) {
      const row = this.inlineState(item)
      if (row.preparing || this.inlineBusy(row) || row.conflict || this.isExcluded(item)) return
      const added = row.images.filter((img) => img.kind === 'new')
      if (!added.length) return
      added.forEach((img) => {
        if (!img.source_id) img.source_id = this.mediaFor(img.mediaKey)?.source_id
      })
      if (added.some((img) => !img.source_id || this.mediaFor(img.mediaKey)?.source_status !== 'ready')) return
      if (!refresh && added.every((img) => row.materials[img.source_id])) return
      if (refresh && row.nextRefresh > Date.now()) return
      const version = row.version,
        scope = JSON.stringify(this.filterPayload())
      row.preparing = true
      row.error = ''
      row.nextRefresh = Date.now() + 10000
      try {
        const data = unwrap(
          await prepareDoudianMainImageMaterials({
            ...this.inlineInput(item),
            source_ids: [...new Set(added.map((img) => img.source_id))],
            request_key: requestKey(),
            action: refresh ? 'refresh' : 'upload'
          })
        )
        if (this._isDestroyed || row.version !== version || scope !== JSON.stringify(this.filterPayload())) return
        ;(data.materials || []).forEach((material) => this.$set(row.materials, material.source_id, material))
      } catch (e) {
        if (!this._isDestroyed && row.version === version) {
          row.error = this.message(e)
          if (e.data?.retry_after_seconds) row.nextRefresh = Date.now() + e.data.retry_after_seconds * 1000
          if (e.data?.current_images) {
            item.old_images = e.data.current_images
            row.conflict = true
          }
        }
      } finally {
        row.preparing = false
        this.saveDraft()
      }
    },
    prepareVisibleInline() {
      if (this.selectedMethod !== 1) return
      ;(this.candidates?.list || []).forEach((item) => {
        if (this.inlineRows[this.productKey(item)]) this.prepareInline(item)
      })
    },
    inlineImageStatus(item, img) {
      if (img.kind === 'existing') return ''
      const row = this.inlineState(item),
        source = this.mediaFor(img.mediaKey),
        material = row.materials[img.source_id]
      if (!material)
        return (
          source?.task_error ||
          (source?.source_status === 'ready' ? '等待上传抖店' : source ? this.sourceState(source) : '图片待登记')
        )
      if (material.can_submit) return '审核通过'
      if (material.status === 'rejected') return `审核驳回：${material.reject_reason || '请替换图片'}`
      return (
        material.last_error_message ||
        material.error_message ||
        (material.upload_status === 'uploaded'
          ? '抖店审核中'
          : material.upload_status === 'unknown'
          ? '上传结果待确认'
          : '正在上传抖店')
      )
    },
    inlineRefreshSeconds(item) {
      return Math.min(10, Math.max(0, Math.ceil((this.inlineState(item).nextRefresh - this.inlineClock) / 1000)))
    },
    async saveInline(item, queryOnly = false) {
      const row = this.inlineState(item)
      if (row.state === 'saving' || (!queryOnly && !this.inlineCanSave(item))) return
      if (!queryOnly)
        row.saveRequest = {
          ...this.inlineInput(item),
          images: row.images.map((img) =>
            img.kind === 'existing'
              ? { kind: 'existing', origin_index: img.origin_index }
              : { kind: 'new', source_id: img.source_id }
          ),
          common_options: { ...this.commonOptions },
          request_key: requestKey()
        }
      if (!row.saveRequest) return
      row.state = 'saving'
      row.error = ''
      this.saveDraft()
      try {
        const data = unwrap(
          await saveDoudianMainImageRow({ ...row.saveRequest, ...(queryOnly ? { query_only: true } : {}) })
        )
        if (this._isDestroyed) return
        if (data.state !== 'completed') throw new Error('保存结果尚未确认')
        this.finishInline(item, data.images)
      } catch (e) {
        if (this._isDestroyed) return
        row.error = this.message(e)
        if (e.data?.current_images) {
          item.old_images = e.data.current_images
          row.conflict = true
        }
        row.batchId = e.data?.batch_id || row.batchId
        row.state =
          e.data?.state === 'confirming'
            ? 'confirming'
            : !e.code || e.code >= 500 || e.code === 404
            ? 'uncertain'
            : 'failed'
      } finally {
        this.saveDraft()
      }
    },
    async queryInlineResult(item) {
      const row = this.inlineState(item)
      if (!row.batchId) return this.saveInline(item, true)
      if (row.preparing || row.nextRefresh > Date.now()) return
      row.preparing = true
      row.nextRefresh = Date.now() + 10000
      try {
        const data = unwrap(await getDoudianBatchDetail(row.batchId, { page: 1, limit: 20 }))
        if (this._isDestroyed) return
        const detail = (data.list || []).find(
          (value) =>
            Number(value.shop_id) === Number(item.shop_id) &&
            String(value.platform_product_id) === String(item.platform_product_id)
        )
        if (detail && Number(detail.status) === 9 && !detail.is_skipped) this.finishInline(item, detail.new_images_json)
        else if (detail && [9, 10, 11].includes(Number(detail.status))) {
          row.error = detail.error_msg || detail.skip_reason || '保存未完成，请核查记录'
          row.state = /人工核查|无法确认|平台已受理/.test(row.error) ? 'uncertain' : 'failed'
        } else row.error = '平台结果待确认，请稍后刷新'
      } catch (e) {
        if (!this._isDestroyed) row.error = this.message(e)
      } finally {
        row.preparing = false
        this.saveDraft()
      }
    },
    finishInline(item, images) {
      if (!Array.isArray(images) || images.length < 3) throw new Error('保存返回的主图不完整，请核查记录')
      this.$set(item, 'old_images', [...images])
      this.$set(item, 'target_images', [...images])
      this.resetInline(item)
      this.inlineRows[this.productKey(item)].state = 'completed'
      this.invalidate()
      this.$message.success('保存成功')
    },
    previewChanges(item) {
      if (this.preview && item.image_changes?.length) return item.image_changes
      const old = item.old_images || []
      if (!this.preview || !Array.isArray(item.target_images) || equal(old, item.target_images)) {
        return old.map((url, index) => ({ key: `image:${index}`, url, change_type: 'unchanged' }))
      }
      // Older preview responses have targets but no presentation metadata.
      const entries = old.map((url, index) => {
        const replacement = this.selectedMethod === 4 ? this.mediaFor(this.replaceMedia[index])?.preview_url : null
        const next = replacement || (this.selectedMethod === 8 && index === 0 ? item.target_images[0] : url)
        return { url: next, replaced: next !== url }
      })
      const used = new Set()
      const images = item.target_images.map((url, index) => {
        const origin = entries.findIndex((entry, i) => !used.has(i) && entry.url === url)
        if (origin >= 0) used.add(origin)
        return {
          key: origin < 0 ? `new:${index}` : `old:${origin}`,
          url,
          change_type:
            origin < 0 ? 'added' : entries[origin].replaced ? 'replaced' : origin === index ? 'unchanged' : 'moved'
        }
      })
      old.forEach((url, index) => {
        if (!used.has(index)) images.push({ key: `deleted:${index}`, url, change_type: 'deleted' })
      })
      return images
    },
    visiblePreviewUrls(item) {
      return this.previewChanges(item)
        .filter((img) => img.change_type !== 'deleted')
        .map((img) => img.url)
    },
    previewImageIndex(item, index) {
      return this.previewChanges(item)
        .slice(0, index)
        .filter((img) => img.change_type !== 'deleted').length
    },
    changeName(type) {
      return { added: '新增', replaced: '替换', moved: '移动', deleted: '已删除' }[type] || ''
    }
  }
}
